import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTicket, replyToTicket, setTicketStatus } from '../api/client';
import type { TicketStatus } from '../types';

const REPLY_STATUS_OPTIONS: TicketStatus[] = ['in_progress', 'resolved', 'closed'];

const STATUS_LABEL: Record<TicketStatus, string> = {
  open:        '🟢 Open',
  in_progress: '🔵 In progress',
  resolved:    '✅ Resolved',
  closed:      '⚪ Closed',
};

function formatDate(ms: number | null): string {
  if (!ms) return '—';
  const d = new Date(ms);
  if (isNaN(d.getTime())) return '—';
  return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)} UTC`;
}

export function TicketDetailPage() {
  const { ticketId = '' } = useParams<{ ticketId: string }>();
  const qc = useQueryClient();

  const { data: ticket, isLoading, error } = useQuery({
    queryKey: ['ticket', ticketId],
    queryFn: () => getTicket(ticketId),
    enabled: !!ticketId,
  });

  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState<TicketStatus>('resolved');
  const [lastResult, setLastResult] = useState<null | { ok: boolean; msg: string }>(null);

  const replyMutation = useMutation({
    mutationFn: () => replyToTicket(ticketId, replyText.trim(), replyStatus),
    onSuccess: (res) => {
      const deliveryNote =
        res.delivery === 'sent'
          ? 'Reply persisted and delivered as Telegram DM.'
          : `Reply persisted but Telegram DM failed: ${res.deliveryError || 'unknown'}. The user can still read it from /setup → ❓ Help & Support → 📋 My tickets.`;
      setLastResult({ ok: true, msg: deliveryNote });
      setReplyText('');
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err) => {
      setLastResult({ ok: false, msg: (err as Error).message || 'Failed to post reply.' });
    },
  });

  // Housekeeping path: changes status only, no reply, no Telegram DM. Kept
  // separate from replyMutation so closing an old backlog can't re-ping
  // customers about tickets they had long forgotten.
  const statusMutation = useMutation({
    mutationFn: (status: TicketStatus) => setTicketStatus(ticketId, status),
    onSuccess: (res) => {
      setLastResult({ ok: true, msg: `Status set to ${STATUS_LABEL[res.status]}. The customer was not notified.` });
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: (err) => {
      setLastResult({ ok: false, msg: (err as Error).message || 'Failed to change status.' });
    },
  });

  if (isLoading) {
    return <div className="mt-6 text-sm text-gray-500">Loading ticket…</div>;
  }
  if (error) {
    return (
      <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(error as Error).message}
      </div>
    );
  }
  if (!ticket) {
    return <div className="mt-6 text-sm text-gray-500">Ticket not found.</div>;
  }

  return (
    <div data-testid="ticket-detail-page">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-gray-900">
          <span className="font-mono text-primary-700">{ticket.ticketId}</span>
        </h1>
        <Link to="/tickets" className="text-sm text-primary-600 hover:underline">
          ← Back to all tickets
        </Link>
      </div>

      <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
        <span><b>Status:</b> {STATUS_LABEL[ticket.status]}</span>
        <span><b>Opened:</b> {formatDate(ticket.createdAt)}</span>
        {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
          <span><b>Last update:</b> {formatDate(ticket.updatedAt)}</span>
        )}
      </div>

      <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
        <span><b>Project:</b> {ticket.projectName || <span className="text-gray-400">—</span>}</span>
        <span><b>Chat:</b> <code className="font-mono text-xs">{ticket.chatId}</code></span>
        <span>
          <b>User:</b>{' '}
          {ticket.username
            ? <span>@{ticket.username}</span>
            : <code className="font-mono text-xs">{ticket.userId}</code>}
        </span>
      </div>

      {/* Conversation thread */}
      <div className="mt-6 space-y-4" data-testid="ticket-thread">
        <ThreadEntry from="user" author={ticket.username || ticket.userId} message={ticket.message} timestamp={ticket.createdAt} />
        {ticket.replies.map((r, i) => (
          <ThreadEntry
            key={i}
            from={r.from}
            author={r.author}
            message={r.message}
            timestamp={r.timestamp}
            deliveryFailed={r.deliveryFailed}
            deliveryError={r.deliveryError ?? null}
          />
        ))}
      </div>

      {/* Reply form */}
      <div className="mt-8 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-700">Reply</h2>
        <p className="mt-1 text-xs text-gray-500">
          The reply is posted into this ticket and delivered to the user as a Telegram DM. If the
          DM bounces (user never opened a private chat with the bot), the reply is still
          available to them via <code>/setup → ❓ Help &amp; Support → 📋 My tickets</code>.
        </p>

        <textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          rows={5}
          maxLength={4000}
          placeholder="Hi,\n\nThanks for reaching out…"
          className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-mono"
          data-testid="reply-textarea"
        />
        <div className="mt-1 text-xs text-gray-400">{replyText.length} / 4000</div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-700">
            <span className="mr-2">Set status to:</span>
            <select
              value={replyStatus}
              onChange={(e) => setReplyStatus(e.target.value as TicketStatus)}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm"
              data-testid="reply-status"
            >
              {REPLY_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <button
            onClick={() => {
              if (!replyText.trim()) return;
              setLastResult(null);
              replyMutation.mutate();
            }}
            disabled={!replyText.trim() || replyMutation.isPending}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            data-testid="reply-submit"
          >
            {replyMutation.isPending ? 'Sending…' : 'Send reply + DM'}
          </button>
        </div>

        <div className="mt-4 border-t border-gray-200 pt-3">
          <div className="text-xs text-gray-500">
            Housekeeping — changes the status only. No reply is recorded and the
            customer is <b>not</b> messaged. Use this for tidying old tickets.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {REPLY_STATUS_OPTIONS.filter((s) => s !== ticket.status).map((s) => (
              <button
                key={s}
                onClick={() => {
                  setLastResult(null);
                  statusMutation.mutate(s);
                }}
                disabled={statusMutation.isPending}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                data-testid={`set-status-${s}`}
              >
                {statusMutation.isPending ? 'Saving…' : `Mark ${STATUS_LABEL[s]} silently`}
              </button>
            ))}
          </div>
        </div>

        {lastResult && (
          <div
            className={`mt-3 rounded-md border p-3 text-sm ${
              lastResult.ok
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
            data-testid="reply-result"
          >
            {lastResult.msg}
          </div>
        )}
      </div>
    </div>
  );
}

interface ThreadEntryProps {
  from: 'user' | 'admin';
  author: string;
  message: string;
  timestamp: number | null;
  deliveryFailed?: boolean;
  deliveryError?: string | null;
}

function ThreadEntry({ from, author, message, timestamp, deliveryFailed, deliveryError }: ThreadEntryProps) {
  const isAdmin = from === 'admin';
  return (
    <div
      className={`rounded-md border p-3 text-sm ${
        isAdmin ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="mb-1 flex items-center justify-between text-xs text-gray-600">
        <span>
          <b>{isAdmin ? '🛟 Support' : '👤 User'}</b>
          <span className="ml-2 text-gray-400">{author}</span>
        </span>
        <span className="text-gray-400">{formatDate(timestamp)}</span>
      </div>
      <div className="whitespace-pre-wrap text-gray-800">{message}</div>
      {isAdmin && deliveryFailed && (
        <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
          ⚠ Telegram DM delivery failed{deliveryError ? `: ${deliveryError}` : ''}. The user can read this reply via <code>/setup → ❓ Help &amp; Support → 📋 My tickets</code>.
        </div>
      )}
    </div>
  );
}
